import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiGet, apiGetJson, apiPut } from "../../utils/apiClient";
import { useUserContext } from "../../contexts/UserContext";

type Level = "info" | "warning" | "critical";
type TargetType = "all" | "company" | "shop";

type MeResponse = {
    ok?: boolean;
    superAdmin?: boolean;
    superAdminGlobal?: boolean;
    superAdminTenants?: string[];
};

type CompanyLite = {
    tenantId?: string;
};

const OEM_TENANT_ID = "ecvision";

export default function SuperNotificationEditPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const user = useUserContext();

    const myTenantId = String((user as any)?.tenantId ?? "").trim();
    const isEcvision = myTenantId === OEM_TENANT_ID;
    const isGlobalSuperUser = (user as any)?.superAdminGlobal === true;
    const isSuperAdmin = (user as any)?.superAdmin === true;
    const [meSuperAdmin, setMeSuperAdmin] = useState(false);
    const [meSuperAdminGlobal, setMeSuperAdminGlobal] = useState(false);
    const [meSuperAdminTenants, setMeSuperAdminTenants] = useState<string[]>([]);
    const superAdminTenants: string[] = Array.isArray((user as any)?.superAdminTenants)
        ? (user as any).superAdminTenants.map((x: any) => String(x ?? "").trim()).filter(Boolean)
        : [];
    const mergedSuperAdmin = isSuperAdmin || meSuperAdmin;
    const mergedSuperAdminGlobal = isGlobalSuperUser || meSuperAdminGlobal;
    const mergedSuperAdminTenants = Array.from(new Set([...superAdminTenants, ...meSuperAdminTenants]));
    const hasEcvisionScope =
        isEcvision ||
        mergedSuperAdminGlobal ||
        mergedSuperAdminTenants.includes(OEM_TENANT_ID);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [tenantId, setTenantId] = useState<string>("");
    const [tenantOptions, setTenantOptions] = useState<string[]>([]);

    const [title, setTitle] = useState("");
    const [body, setBody] = useState("");
    const [level, setLevel] = useState<Level>("info");

    const [isGlobal, setIsGlobal] = useState(false);

    const [targetType, setTargetType] = useState<TargetType>("all");
    const [targetIdsRaw, setTargetIdsRaw] = useState("");
    const [isActive, setIsActive] = useState(true);
    const [loadedDocTenantId, setLoadedDocTenantId] = useState("");

    const [canEdit, setCanEdit] = useState<boolean>(true);

    const canToggleGlobal = useMemo(() => {
        // スーパーユーザーは全体通知への昇格を許可（API側でtenant昇格を実施）
        return (
            hasEcvisionScope
        );
    }, [hasEcvisionScope]);

    useEffect(() => {
        let cancelled = false;

        (async () => {
            try {
                const me = await apiGetJson<MeResponse>("/api/me");
                if (cancelled) return;
                setMeSuperAdmin(me?.superAdmin === true);
                setMeSuperAdminGlobal(me?.superAdminGlobal === true);
                setMeSuperAdminTenants(
                    Array.isArray(me?.superAdminTenants)
                        ? me.superAdminTenants.map((x) => String(x ?? "").trim()).filter(Boolean)
                        : []
                );
            } catch {
                if (cancelled) return;
            }
        })();

        return () => {
            cancelled = true;
        };
    }, []);

    /* 初期ロード */
    useEffect(() => {
        if (!id) return;

        async function load() {
            try {
                const res = await apiGet(`/api/super/notifications/${id}`);
                const n = await res.json();

                setTenantId(String(n.tenantId ?? "").trim());
                setLoadedDocTenantId(String(n.tenantId ?? "").trim());

                setTitle(n.title ?? "");
                setBody(n.body ?? "");
                setLevel(n.level ?? "info");

                setIsGlobal(n.isGlobal === true);

                setTargetType(n.targetType ?? "all");
                setIsActive(!!n.isActive);
                setTargetIdsRaw(Array.isArray(n.targetIds) ? n.targetIds.join("\n") : "");

                // APIが canEdit を返す前提（保険あり）
                if (typeof n.canEdit === "boolean") {
                    const docTenant = String(n.tenantId ?? "").trim();
                    setCanEdit(
                        n.canEdit ||
                        mergedSuperAdminGlobal ||
                        docTenant === myTenantId ||
                        mergedSuperAdminTenants.includes(docTenant)
                    );
                } else {
                    const docTenant = String(n.tenantId ?? "").trim();
                    setCanEdit(
                        mergedSuperAdminGlobal ||
                        docTenant === myTenantId ||
                        mergedSuperAdminTenants.includes(docTenant)
                    );
                }
            } catch {
                setError("データの取得に失敗しました");
            } finally {
                setLoading(false);
            }
        }

        load();
    }, [id]);

    useEffect(() => {
        if (!loadedDocTenantId) return;
        const elevated =
            mergedSuperAdminGlobal ||
            loadedDocTenantId === myTenantId ||
            mergedSuperAdminTenants.includes(loadedDocTenantId);
        if (elevated) {
            setCanEdit((prev) => prev || true);
        }
    }, [loadedDocTenantId, mergedSuperAdminGlobal, mergedSuperAdminTenants, myTenantId]);

    useEffect(() => {
        let cancelled = false;

        (async () => {
            try {
                const rows = await apiGetJson<CompanyLite[]>("/api/super/companies");
                if (cancelled) return;

                const set = new Set<string>();
                if (mergedSuperAdminGlobal) {
                    for (const row of rows ?? []) {
                        const t = String(row?.tenantId ?? "").trim();
                        if (t) set.add(t);
                    }
                } else {
                    if (myTenantId) set.add(myTenantId);
                    for (const t of mergedSuperAdminTenants) {
                        if (t) set.add(t);
                    }
                }

                if (tenantId) set.add(tenantId);
                setTenantOptions(Array.from(set).sort((a, b) => a.localeCompare(b, "ja")));
            } catch {
                if (cancelled) return;
                const base = [tenantId, myTenantId, ...mergedSuperAdminTenants].filter(Boolean) as string[];
                setTenantOptions(Array.from(new Set(base)));
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [mergedSuperAdminGlobal, mergedSuperAdminTenants, myTenantId, tenantId]);

    const submit = async () => {
        if (!id) return;

        const effectiveTargetType: TargetType = isGlobal ? "all" : targetType;

        const targetIds =
            effectiveTargetType === "all"
                ? []
                : targetIdsRaw
                    .split("\n")
                    .map((s) => s.trim())
                    .filter(Boolean);

        try {
            setSaving(true);

            const payload: any = {
                title,
                body,
                level,
                targetType: effectiveTargetType,
                targetIds,
                isActive,
                tenantId,
            };

            // 全体フラグは ecvision & docTenant=ecvision のときだけ送る
            if (canToggleGlobal) payload.isGlobal = isGlobal;

            const res = await apiPut(`/api/super/notifications/${id}`, payload);
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                throw new Error(body?.error || `save_failed_${res.status}`);
            }
            navigate("/super/notifications");
        } catch (e: any) {
            setError(`保存に失敗しました: ${e?.message ?? "unknown"}`);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div>Loading...</div>;

    return (
        <div className="max-w-3xl space-y-6">
            <h1 className="text-lg font-semibold">お知らせ 編集</h1>

            <div className="text-[11px] text-gray-500 border rounded px-2 py-1 bg-gray-50">
                debug: tenant={myTenantId || "-"} / docTenant={tenantId || "-"} / canEdit={String(canEdit)} / canToggleGlobal={String(canToggleGlobal)} / super={String(mergedSuperAdmin)} / global={String(mergedSuperAdminGlobal)} / superTenants={mergedSuperAdminTenants.join(",") || "-"}
            </div>

            {error && (
                <div className="text-sm text-red-600 border border-red-300 bg-red-50 px-3 py-2 rounded">
                    {error}
                </div>
            )}

            <div className="text-sm text-gray-600">
                作成テナント: <span className="font-semibold">{tenantId || "-"}</span>
                {isGlobal && (
                    <span className="ml-2 text-[11px] px-2 py-0.5 rounded bg-blue-50 border border-blue-200 text-blue-700">
                        全体通知
                    </span>
                )}
                {!canEdit && (
                    <span className="ml-2 text-[11px] px-2 py-0.5 rounded bg-gray-50 border text-gray-600">
                        閲覧のみ（編集不可）
                    </span>
                )}
            </div>

            {canEdit && tenantOptions.length > 0 && (
                <div className="space-y-1">
                    <label className="text-sm font-medium">作成テナント</label>
                    <select
                        className="border rounded px-3 py-2 text-sm min-w-[260px]"
                        value={tenantId}
                        onChange={(e) => setTenantId(e.target.value)}
                    >
                        {tenantOptions.map((t) => (
                            <option key={t} value={t}>
                                {t}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            {/* 全体表示（ecvision & docTenant=ecvision のみ編集可能） */}
            {canToggleGlobal && (
                <label className="flex items-center gap-2">
                    <input
                        type="checkbox"
                        checked={isGlobal}
                        disabled={!canToggleGlobal || !canEdit}
                        onChange={(e) => setIsGlobal(e.target.checked)}
                    />
                    <span className="text-sm">
                        全テナントに表示（ecvision 全体通知）
                    </span>
                </label>
            )}

            {canToggleGlobal && tenantId !== OEM_TENANT_ID && (
                <div className="text-[11px] text-gray-600">
                    ※全テナント表示をONにすると、ecvision / ecx を含む全テナントに表示されます。管理上、保存時にこの通知は ecvision テナントの全体通知として扱います。
                </div>
            )}

            {/* タイトル */}
            <div className="space-y-1">
                <label className="text-sm font-medium">タイトル *</label>
                <input
                    className="w-full border rounded px-3 py-2 text-sm"
                    value={title}
                    disabled={!canEdit}
                    onChange={(e) => setTitle(e.target.value)}
                />
            </div>

            {/* 本文 */}
            <div className="space-y-1">
                <label className="text-sm font-medium">本文</label>
                <textarea
                    className="w-full border rounded px-3 py-2 text-sm min-h-[120px]"
                    value={body}
                    disabled={!canEdit}
                    onChange={(e) => setBody(e.target.value)}
                />
                ※1行表示の短文はテキスト可／改行・複数行はHTML必須
            </div>

            {/* 重要度 */}
            <div className="space-y-1">
                <label className="text-sm font-medium">重要度</label>
                <select
                    className="border rounded px-3 py-2 text-sm"
                    value={level}
                    disabled={!canEdit}
                    onChange={(e) => setLevel(e.target.value as Level)}
                >
                    <option value="info">info（情報）</option>
                    <option value="warning">warning（注意）</option>
                    <option value="critical">critical（重要）</option>
                </select>
            </div>

            {/* 対象 */}
            <div className="space-y-1">
                <label className="text-sm font-medium">対象</label>
                <select
                    className="border rounded px-3 py-2 text-sm"
                    value={isGlobal ? "all" : targetType}
                    disabled={!canEdit || isGlobal}
                    onChange={(e) => setTargetType(e.target.value as TargetType)}
                >
                    <option value="all">全ユーザー</option>
                    {/*<option value="company">特定会社</option>*/}
                    <option value="shop">特定ショップ</option>
                </select>
                {isGlobal && (
                    <div className="text-[11px] text-gray-600">
                        全体通知は “全ユーザー” 固定です（対象ショップ指定は不可）
                    </div>
                )}
            </div>

            {!isGlobal && targetType !== "all" && (
                <div className="space-y-1">
                    <label className="text-sm font-medium">対象ID（1行1つ）</label>
                    <textarea
                        className="w-full border rounded px-3 py-2 text-sm min-h-[80px]"
                        value={targetIdsRaw}
                        disabled={!canEdit}
                        onChange={(e) => setTargetIdsRaw(e.target.value)}
                        placeholder={targetType === "company" ? "companyId を1行ずつ入力" : "shopUrl を1行ずつ入力"}
                    />
                </div>
            )}

            {/* 有効 */}
            <label className="flex items-center gap-2">
                <input
                    type="checkbox"
                    checked={isActive}
                    disabled={!canEdit}
                    onChange={(e) => setIsActive(e.target.checked)}
                />
                <span className="text-sm">有効にする</span>
            </label>

            <div className="flex gap-3">
                {canEdit ? (
                    <button
                        onClick={submit}
                        disabled={saving}
                        className="px-4 py-2 rounded bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-50"
                    >
                        保存
                    </button>
                ) : (
                    <button
                        disabled
                        className="px-4 py-2 rounded bg-gray-200 text-gray-600 text-sm"
                        title="この通知は編集できません"
                    >
                        保存
                    </button>
                )}

                <button
                    onClick={() => navigate("/super/notifications")}
                    className="px-4 py-2 rounded border text-sm"
                >
                    戻る
                </button>
            </div>
        </div>
    );
}
